import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Tabs,
} from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { DataStore } from 'app/common/data-storage/features/data-storage';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import * as PageNotifications from 'app/common/notifications/features/page-notifications';
import { UserRoleList } from 'app/railways/domains/access/features/roles/role-list';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { TUser } from 'app/railways/domains/users/core/user';
import {
  getUserRequest,
  updateUserRequest,
} from 'app/railways/domains/users/requests';

import { Loading } from '../../../../ui';
import { CanAccess } from '../../access/features/permissions';

const { TabPane } = Tabs;

const ButtonSubmit = observer(() => {
  return (
    <CanAccess perm="PERM_USER_UPDATE">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" htmlType="submit">
          Сохранить
        </Button>
      </div>
    </CanAccess>
  );
});

type UpdatedUser = Partial<TUser> & { id: number };

type UserDetailsProps = {
  id: number;
};

export const UserDetails = observer(({ id }: UserDetailsProps) => {
  const [form] = Form.useForm();

  const [userStore] = useState(
    () => new DataStore<TUser>({ id: 0, blocked: false, login: '', roles: [] }),
  );
  const authStatusStore = useAuthStatusWriter();
  const pageNotify = usePageNotify();

  const addUserResource = useResource<TUser['id'], TUser>({
    request: getUserRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (user) => {
      userStore.setData(user);
      form.setFieldsValue(user);
    },
  });

  const updateUserResource = useResource<UpdatedUser, TUser>({
    request: updateUserRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (user) => {
      userStore.setData(user);
      form.setFieldsValue(user);
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные пользователя обновлены',
      });
    },
  });

  const isLoading = addUserResource.isLoading || updateUserResource.isLoading;

  useEffect(() => {
    addUserResource.send(id);
  }, []);

  const handleUpdateUser = useCallback(
    (updatedUser: UpdatedUser) => {
      updateUserResource.send({ ...userStore.data, ...updatedUser });
    },
    [updateUserResource, userStore.data],
  );

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <PageNotifications.Render>{(notify) => notify}</PageNotifications.Render>

      <Card size="small" bodyStyle={{ paddingTop: 0 }}>
        <Loading active={isLoading}>
          <Tabs>
            <TabPane forceRender tab="Данные аккаунта" key="1">
              {/* @TODO: Че т не рендерится при первом рендере */}
              <Form form={form} layout="vertical" onFinish={handleUpdateUser}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="login"
                      label="Логин"
                      rules={[{ required: true }]}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item name="password" label="Пароль">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item name="blocked" valuePropName="checked">
                      <Checkbox>Заблокирован</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider />
                <ButtonSubmit />
              </Form>
            </TabPane>

            <TabPane tab="Роли и разрешения" key="2">
              <CanAccess
                perm="PERM_USER_ROLE_READ"
                fallback="У вас нет доступа к этим данным"
              >
                <Form layout="vertical" onFinish={handleUpdateUser}>
                  <Form.Item
                    name="roles"
                    // valuePropName="selected"
                    rules={[
                      {
                        required: true,
                        message: 'Пожалуйста, выберите необходимые роли',
                      },
                    ]}
                  >
                    <UserRoleList selected={userStore.data.roles} />
                  </Form.Item>

                  <Divider />

                  <CanAccess perm="PERM_USER_ROLE_UPDATE">
                    <ButtonSubmit />
                  </CanAccess>
                </Form>
              </CanAccess>
            </TabPane>
          </Tabs>
        </Loading>
      </Card>
    </Space>
  );
});
